import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { MuteScenariosReason } from 'features/residents/models/mute-scenarios-reason';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type UseMuteScenariosConfigurationReturn = {
  expanded: boolean;
  toggleExpand: () => void;
  handleReasonChange: (newReason: MuteScenariosReason) => void;
  validationRules: {
    until: {
      validate: (value: string | null | undefined) => string | boolean;
    };
  };
};

const DefaultVisitorMuteDurationInHours = 4;

const useMuteScenariosConfiguration = (): UseMuteScenariosConfigurationReturn => {
  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);

  const [expanded, setExpanded] = useState(false);
  const scenariosMuted = residentSettingEditForm.watch('scenariosMuted');

  residentSettingEditForm.register('muteScenariosReason', {
    validate: (value) => {
      if (!scenariosMuted) return true;
      if (!value) return t('muteScenarios.reasonRequired');
      return true;
    },
  });

  const validationRules = {
    until: {
      validate: (value: string | null | undefined): string | boolean => {
        if (!scenariosMuted) {
          return true;
        }

        if (!value) {
          return t('muteScenarios.untilRequired');
        }

        const selectedDate = moment(value);
        const minDate = moment().add(5, 'minutes');
        const maxDate = moment().add(1, 'month');

        if (selectedDate.isBefore(minDate)) {
          return t('muteScenarios.untilMinFiveMinutes');
        }

        if (selectedDate.isAfter(maxDate)) {
          return t('muteScenarios.untilMaxOneMonth');
        }

        return true;
      },
    },
  };

  useEffect(() => {
    setExpanded(!!scenariosMuted);
  }, [scenariosMuted]);

  const toggleExpand = (): void => {
    if (scenariosMuted) {
      setExpanded(!expanded);
    }
  };

  const handleReasonChange = (newReason: MuteScenariosReason): void => {
    residentSettingEditForm.setValue('muteScenariosReason', newReason, { shouldDirty: true, shouldValidate: true });

    if (newReason === MuteScenariosReason.Visitor && !residentSettingEditForm.getValues('muteScenariosUntil')) {
      residentSettingEditForm.setValue('muteScenariosUntil', moment().add(DefaultVisitorMuteDurationInHours, 'hours').toISOString(), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return {
    expanded,
    toggleExpand,
    handleReasonChange,
    validationRules,
  };
};

export default useMuteScenariosConfiguration;
